// farm-booking/frontend/src/App.js
// Application unifiée avec AuthContext + MessageBox
// 73+ Papy
// ✅ AJOUT : MessageBoxProvider pour les popups personnalisées

import React, { useState, useEffect } from 'react'
import ModeTestBandeau from './components/ModeTestBandeau'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { MessageBoxProvider } from './components/MessageBox'
import HomePage from './pages/HomePage'
import Maintenance from './pages/Maintenance'
import AdminSite from './pages/AdminSite'
import AuthPage from './pages/AuthPage'
import GestionComptoir from './modules/admin/components/GestionComptoir';
import SimulateurCultural from './modules/admin/components/SimulateurCultural';
import SimulateurCulturalV2 from './modules/admin/components/SimulateurCulturalV2';
import CatalogModule from './modules/client/CatalogModule'
import CartPage from './modules/client/CartPage'
import ProfileModule from './modules/client/ProfileModule'
import AdminDashboard from './modules/admin/AdminDashboard'
import RetraitValidation from './pages/RetraitValidation';
import AutoLogin from './pages/AutoLogin';
import GestionSemences from './modules/admin/components/GestionSemences';
import GestionLogin from './pages/GestionLogin';
import { GrafcetEditor } from './modules/grafcet';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import FichesCulturales from './components/FichesCulturales';
import GestionParcelles from './modules/admin/components/GestionParcelles';
import SupervisionIoT from './modules/admin/components/SupervisionIoT';

// Hook pour vérifier le mode maintenance
import { useMaintenanceMode } from './hooks/useMaintenanceMode'

// ✅ Définition de l'API Base URL
const API_BASE = '/api';

// ✅ Composant wrapper pour les routes client (gère le state global)
function ClientRoutes() {
  const { user, logout, isAuthenticated } = useAuth();
  const [cart, setCart] = useState([]);
  const [config, setConfig] = useState({});
  const [periodeActive, setPeriodeActive] = useState(null);

  // ✅ Composant de protection des routes
  const ProtectedRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/auth" replace />;
    }
    return children;
  };

  // Charger la configuration au montage
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/configuration`);
        if (response.ok) {
          const result = await response.json();
          
          // ⚡ CONVERSION : tableau → objet
          const configObj = {};
          const configArray = result.data || result;
          
          if (Array.isArray(configArray)) {
            configArray.forEach(item => {
              configObj[item.param_name] = item.param_value;
            });
          }
          
          setConfig(configObj);
          
          // 🟡 Log si mode test actif
          if (configObj.mode_test === 'oui') {
            console.warn('🟡 MODE TEST ACTIF - Contraintes workflow bypassed');
          }
        }
      } catch (err) {
        console.error('Erreur chargement config:', err);
        setConfig({ mode_test: 'non' }); // Config par défaut
      }
    };
    loadConfig();
  }, []);


  // Charger la période active - ✅ CORRIGÉ
  useEffect(() => {
    const loadPeriodeActive = async () => {
      try {
        const response = await fetch(`${API_BASE}/periodes/active`);
        if (response.ok) {
          const result = await response.json();
          // ✅ Utiliser result.periode (pas result.data)
          setPeriodeActive(result.periode || result.data || null);
        }
      } catch (err) {
        console.error('Erreur chargement période:', err);
      }
    };
    loadPeriodeActive();
  }, []);

  // ============================================
  // FONCTIONS DE GESTION DU PANIER
  // ============================================
  
  const addToCart = (produit, quantite = 1) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === produit.id);
      if (existing) {
        return prevCart.map(item =>
          item.id === produit.id
            ? { ...item, quantite: item.quantite + quantite }
            : item
        );
      }
      return [...prevCart, { ...produit, quantite }];
    });
  };

  const updateCartQuantity = (produitId, quantite) => {
    if (quantite <= 0) {
      setCart(prevCart => prevCart.filter(item => item.id !== produitId));
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === produitId ? { ...item, quantite } : item
        )
      );
    }
  };

  // ✅ NOUVEAU : Supprimer un item du panier
  const removeFromCart = (produitId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== produitId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const handleLogout = () => {
    clearCart();
    logout();
  };

  // ============================================
  // FONCTIONS DE CONFIRMATION RÉSERVATION
  // ============================================
  
  // ✅ NOUVEAU : Confirmer la réservation
  const handleConfirmReservation = async (navigate) => {
    if (cart.length === 0) {
      alert('Votre panier est vide');
      return;
    }
    
    if (!periodeActive) {
      alert('Aucune période de réservation active');
      return;
    }

    try {
      const reservationData = {
        code_client: user.code_client,
        periode_id: periodeActive.id,
        produits: cart.map(item => ({
          code_produit: item.code_produit,
          quantite: item.quantite
        }))
      };

      const response = await fetch(`${API_BASE}/reservations/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData)
      });

      const result = await response.json();
     
      if (result.success) {
        const total = cart.reduce((sum, item) => sum + (Number(item.prix_ttc) * item.quantite), 0);
        alert(`✅ Réservation confirmée !\nNuméro: ${result.numero_reservation}\nTotal: ${total.toFixed(2)}€`);
        clearCart();
        navigate('/catalog');
      } else {
        alert(`❌ Erreur: ${result.message}`);
      }      

    } catch (err) {
      alert('❌ Erreur: ' + err.message);
      console.error('Erreur réservation:', err);
    }
  };

  return (
    <>
      {/* ⚡ BANDEAU MODE TEST - Affiche automatiquement si mode_test='oui' */}
      <ModeTestBandeau config={config} />
      
      <Routes>
      {/* Page d'accueil publique avec OSM */}
      <Route path="/" element={<HomePage />} />
      
      {/* Authentification clients - AVEC CONTEXTE */}
      <Route path="/auth" element={<AuthPage />} />
      
      {/* 🆕 AUTO-LOGIN par QR Code URL (Magic Link) */}
      {/* Format: https://lafermedupetitessart.fr/auto/CLI-XXXXXX/QR-XXXXXX */}
      <Route path="/auto/:code_client/:token" element={<AutoLogin />} />
      
      {/* Espace client - AVEC PROPS ET PROTECTION */}
      <Route 
        path="/catalog" 
        element={
          <ProtectedRoute>
            <CatalogModule 
              user={user}
              config={config}
              onLogout={handleLogout}
              apiBase={API_BASE}
              cart={cart}
              setCart={setCart}
              addToCart={addToCart}
              updateCartQuantity={updateCartQuantity}
              periodeActive={periodeActive}
            />
          </ProtectedRoute>
        } 
      />
      
      {/* ✅ CORRIGÉ : CartPage avec les bonnes props */}
      <Route 
        path="/cart" 
        element={
          <ProtectedRoute>
            <CartPageWrapper 
              user={user}
              config={config}
              apiBase={API_BASE}
              cart={cart}
              setCart={setCart}
              periodeActive={periodeActive}
              onUpdateQuantity={updateCartQuantity}
              onRemoveItem={removeFromCart}
              onConfirmReservation={handleConfirmReservation}
            />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfileModule 
              user={user}
              config={config}
              onLogout={handleLogout}
              apiBase={API_BASE}
            />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Farm-Booking - VRAI ADMIN */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboard apiBase={API_BASE} />
          </AdminProtectedRoute>
        }
      />
      
      <Route path="/admin/comptoir" element={<AdminProtectedRoute><GestionComptoir /></AdminProtectedRoute>} />
      <Route path="/admin/cultures_demo" element={<AdminProtectedRoute><SimulateurCultural /></AdminProtectedRoute>} />
      <Route path="/admin/cultures" element={<AdminProtectedRoute><SimulateurCulturalV2 /></AdminProtectedRoute>} />
      <Route path="/admin/grafcet" element={<AdminProtectedRoute><GrafcetEditor /></AdminProtectedRoute>} />
      <Route path="/admin/fiches-culturales" element={<AdminProtectedRoute><FichesCulturales /></AdminProtectedRoute>} />
      <Route path="/retrait/:code_client/:numero_reservation" element={<RetraitValidation />} />
      <Route path="/admin/parcelles" element={<AdminProtectedRoute><GestionParcelles /></AdminProtectedRoute>} />
      <Route path="/admin-site" element={<AdminProtectedRoute><AdminSite /></AdminProtectedRoute>} />
      <Route path="/admin/supervision-iot" element={<SupervisionIoT />} />
      <Route path="/maintenance" element={<Maintenance />} />
      <Route path="/admin/supervision-iot" element={<SupervisionIoT />} />

      {/* Redirections pour compatibilité */}
      <Route path="/reservations" element={<Navigate to="/catalog" replace />} />
      <Route path="/admin-farm" element={<Navigate to="/admin" replace />} />
      <Route path="/login.html" element={<Navigate to="/auth" replace />} />
      <Route path="/admin.html" element={<Navigate to="/admin" replace />} />
      <Route path="/semences" element={<GestionSemences />} />
      <Route path="/gestion" element={<GestionLogin />} />

      {/* 404 -> Accueil */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// ✅ NOUVEAU : Wrapper pour CartPage avec navigation
function CartPageWrapper({ onConfirmReservation, ...props }) {
  const navigate = useNavigate();
  
  return (
    <CartPage 
      {...props}
      onConfirmReservation={() => onConfirmReservation(navigate)}
      onBack={() => navigate('/catalog')}
    />
  );
}

function App() {
  const { isMaintenanceMode, loading } = useMaintenanceMode()

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    )
  }

  // Mode maintenance : toutes les pages redirigent vers Maintenance sauf admin
  if (isMaintenanceMode) {
    return (
      <AuthProvider>
        <MessageBoxProvider>
          <Router>
            <Routes>
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard apiBase={API_BASE} /></AdminProtectedRoute>} />
              <Route path="/admin-site" element={<AdminProtectedRoute><AdminSite /></AdminProtectedRoute>} />
              <Route path="*" element={<Maintenance />} />
            </Routes>
          </Router>
        </MessageBoxProvider>
      </AuthProvider>
    )
  }

  // Mode normal
  return (
    <AuthProvider>
      <MessageBoxProvider>
        <Router>
          <ClientRoutes />
        </Router>
      </MessageBoxProvider>
    </AuthProvider>
  )
}

export default App
